import {
    Component, ChangeDetectionStrategy, Input, Output,
    EventEmitter, OnChanges, HostListener, OnDestroy, SimpleChanges
} from '@angular/core';
import { UserFilter } from '../../../covance-filter/filters-storage';
import { Subscription } from 'rxjs';
import { SampleTrackingSelectFilterService } from '../sample-tracking-select/sample-tracking-select-filter-service';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { FilterLabels } from '../../reports/interfaces/filter-data';

@Component({
    selector: 'app-my-filters',
    template: require('./my-filters.component.html'),
    styles: [require('./my-filters.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class MyFiltersComponent implements OnChanges, OnDestroy {

    @Input()
    public filterInfo: FilterLabels;

    @Input()
    public showLoading: boolean;

    @Input()
    public filterItems: UserFilter[] = [];

    @Input()
    public preSelectedFilterItem: UserFilter;

    @Output()
    public onMyFilterSubmit: EventEmitter<{ action: string, userFilter: UserFilter }> = new EventEmitter<{ action: string, userFilter: UserFilter }>();

    @Output()
    public onMyFilterChange: EventEmitter<UserFilter> = new EventEmitter<UserFilter>();

    @Output()
    public onMyFilterAdd: EventEmitter<UserFilter> = new EventEmitter<UserFilter>();

    public errorMsg = '';
    public selectedFilterName: FormControl = new FormControl('', [this.isUniqueFilterName()]);
    public searchFilterName: FormControl = new FormControl('', [this.isUniqueFilterName()]);
    public searchfilterItems: UserFilter[] = [];
    public showDropdownList = false;
    public showMenu = false;
    public showFilterInput = false;
    public selectedFilter: UserFilter;
    public filterMenuOptions = {
        action: 'new',
        activeButtonText: 'Save',
        warnButtonText: 'Discard'
    };
    public isExactFilterNameFound = false;
    private subscription: Subscription;

    constructor(
        private selectFilterService: SampleTrackingSelectFilterService
    ) {
        this.subscription = this.selectFilterService.resetMyFilters.subscribe(
            (res: boolean) => {
                if (res) {
                    this.selectedFilter = undefined;
                }
            });
    }

    @HostListener('document:click', ['$event.target'])
    public onOutsideClick(targetElement: HTMLElement): void {
        if ((targetElement.className.indexOf('action') === -1) &&
            (targetElement.offsetParent &&
                targetElement.offsetParent.className.indexOf('select') === -1)) {
            const isFilterMenuActive = document.getElementsByClassName('select-menu').length;
            if (isFilterMenuActive) {
                this.hideMenu();
            }
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.filterItems && this.filterItems && this.filterItems.length) {
            this.getFilters();
            if (this.selectedFilter && this.selectedFilter.id) {
                this.onFilterItemSelect(this.selectedFilter);
            }
        }
        if (changes.preSelectedFilterItem && this.preSelectedFilterItem) {
            this.onFilterItemSelect(this.preSelectedFilterItem);
            this.selectedFilterName.setValue(this.preSelectedFilterItem.name);
        }
    }

    public onFilterMenuClick(action: string) {
        this.showMenu = false;
        this.showDropdownList = false;
        this.showFilterInput = true;
        this.errorMsg = null;

        if (action === 'close') {
            this.showFilterInput = false;
            return;
        }

        this.filterMenuOptions = {
            action,
            activeButtonText: action === 'delete' ? 'No' : 'Save',
            warnButtonText: action === 'delete' ? 'Yes' : 'Discard'
        };

        if (action === 'new') {
            this.selectedFilterName.setValue(this.filterInfo.newText);
            this.selectedFilter = { name: this.selectedFilterName.value, params: {} } as UserFilter;
        }

        if (action === 'delete') {
            this.selectedFilterName.setErrors(null);
        }
    }

    public onFilterItemSelect(selectedItem: UserFilter) {
        this.selectedFilter = selectedItem;
        this.hideMenu();
        if (this.selectedFilter && this.selectedFilter.id) {
            this.selectedFilterName.setValue(this.selectedFilter.name);
            this.onMyFilterChange.emit(this.selectedFilter);
        } else {
            this.onMyFilterAdd.emit(this.selectedFilter);
        }
    }

    public onSubmitButtonClick(action: string) {
        if (action === 'delete') {
            this.hideMenu();
            return;
        }

        this.errorMsg = null;
        this.selectedFilter.name = this.selectedFilterName.value;
        if (!this.filterItems.find(fi => fi.name === this.selectedFilter.name)) {
            this.filterItems.push(this.selectedFilter);
        }
        this.onMyFilterSubmit.emit({ action, userFilter: this.selectedFilter });
        this.hideMenu();
        return;
    }

    public onCloseButtonClick(action: string) {
        if (action === 'delete') {
            if (this.selectedFilter.id) {
                this.onMyFilterSubmit.emit({ action, userFilter: this.selectedFilter });
                this.selectedFilter = undefined;
            } else {
                this.filterItems = this.filterItems.filter(fi => fi.name !== this.selectedFilter.name);
                this.selectedFilterName.setValue('');
                this.selectedFilter = undefined;
            }
        }
        this.hideMenu();
    }

    public onSelectboxClick() {
        this.showDropdownList = !this.showDropdownList;
        this.searchFilterName.setValue('');
        this.searchfilterItems = this.filterItems;
        const focusElement = document.getElementById('filterSearchBox');
        if (focusElement) {
            setTimeout(
                () => {
                    focusElement.focus();
                },
                0);
        }
    }

    public onAddNewItem() {
        this.showDropdownList = false;
        this.selectedFilterName.setValue(this.searchFilterName.value);
        this.selectedFilter = { name: this.searchFilterName.value, params: {} } as UserFilter;

        this.onSubmitButtonClick('new');
        this.onMyFilterAdd.emit(this.selectedFilter);
    }

    public onSearchBoxChange() {
        if (this.filterItems) {
            this.searchfilterItems = this.filterItems.filter(item => item.name.toLowerCase().indexOf(this.searchFilterName.value.toLowerCase()) !== -1);
            this.isExactFilterNameFound = !!this.filterItems.find(item => item.name.toLowerCase() === this.searchFilterName.value.toLowerCase());
        }
    }

    public ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    private hideMenu() {
        this.showMenu = false;
        this.showFilterInput = false;
        this.showDropdownList = false;
    }

    private getFilters() {
        if (this.filterItems && this.filterItems.length) {
            this.searchfilterItems = this.filterItems;
            this.selectedFilter = this.filterItems.find(fi => fi.name === this.selectedFilterName.value);
        }
    }

    private isUniqueFilterName(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value) {
                const value = control.value.trim();

                if (!value) {
                    return { validation: 'Filter name required.' };
                }

                if (value && this.filterItems && this.filterItems.find(fi => fi.name === value)) {
                    return { validation: 'Filter name already exist.' };
                }
            }
            return null;
        };
    }
}
